/**
 * useMembraneSetup
 *
 * Shared composable for integration management pages (MembraneIntegration,
 * IntegrationSyncWorkflow and future per-integration workflows). Handles all data loading
 * and helper functions that are otherwise duplicated across those components.
 */

import {
  Connection,
  Integration,
  WorkspaceElementState,
} from "@membranehq/sdk";
import { match, P } from "ts-pattern";
import {
  computed,
  ComputedRef,
  defineComponent,
  h,
  onMounted,
  PropType,
  ref,
  Ref,
} from "vue";
import { useModal } from "vue-final-modal";
import type { RouteLocationNormalizedLoaded, Router } from "vue-router";

import type {
  Controller,
  IntegrationAppEnabledDataCollection,
  IntegrationAppEnabledDataCollectionId,
  ReleasableRef,
} from "@ctrl/controller-rpc";

import { copy } from "@/clipboard";
import { type ButtonState } from "@/components/base/BaseButton.vue";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import type { UseIntegrations } from "@/components/integrations/useIntegrations";
import Modal from "@/components/modals/Modal.vue";
import ConnectionTypePicker from "@/components/settings/integrations/ConnectionTypePicker.vue";
import {
  patchConnectionConnectorVersion,
  runConnectionVersionUpdate,
} from "@/components/settings/integrations/connectionVersionUpdate";
import { dataCollectionTableId } from "@/components/settings/integrations/dataCollection";
import { getAllowedDataCollections } from "@/components/settings/integrations/integrationAllowlist";
import { releaseBestEffort } from "@/ref";
import { RouteNames } from "@/routes/types";
import { connectInBrowser } from "@/utils/membrane";
import { navigateToDatabase } from "@/utils/navigate";
import { errorMessage, firstStringProperty } from "@/utils/unknown";

// ── Types ──────────────────────────────────────────────────────────────────

export type ConnectionOption = {
  key?: string;
  type: string;
  title?: string;
  inputSchema?: {
    properties?: Record<string, { type?: string; title?: string }>;
    required?: string[];
  };
};

export type AuthInputField = { key: string; label: string };

/**
 * Splits an auth option's input fields into the ones worth asking for *before*
 * the OAuth redirect and the ones that can wait.
 *
 * Only fields listed in the schema's `required` array justify interrupting the
 * user upfront. Jira's `site_url` is the motivating case: it is optional
 * because the connector resolves the site from the token whenever the account
 * can reach exactly one — the common case — so prompting everybody made most
 * users answer a question they had no way to answer yet. Optional fields are
 * collected only after an attempt fails in a way that proves they are needed.
 */
export function partitionAuthInputFields(
  inputSchema: ConnectionOption["inputSchema"]
): { required: AuthInputField[]; optional: AuthInputField[] } {
  const required = new Set(inputSchema?.required ?? []);
  const fields = Object.entries(inputSchema?.properties ?? {}).map(
    ([key, schema]) => ({ key, label: schema.title ?? key })
  );
  return {
    required: fields.filter((field) => required.has(field.key)),
    optional: fields.filter((field) => !required.has(field.key)),
  };
}

export type WorkspaceDataItem = {
  workspaceId: string;
  workspaceName: string;
  membraneData: Awaited<
    ReturnType<
      import("@ctrl/controller-rpc").Controller["integrationApp"]["get"]
    >
  >;
};

export type EnabledDataCollectionWithWorkspace = {
  workspaceId: string;
  workspaceName: string;
  dataCollectionId: IntegrationAppEnabledDataCollectionId;
  dataCollection: IntegrationAppEnabledDataCollection;
  databaseName: string;
};

// ── ExtraFieldsModalBody (used only internally by addAccount) ──────────────

const ExtraFieldsModalBody = defineComponent({
  name: "ExtraFieldsModalBody",
  props: {
    extraFields: {
      type: Array as PropType<Array<{ key: string; label: string }>>,
      default: () => [],
    },
    extraValues: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({}),
    },
  },
  emits: {
    "update:extraValues": (_values: Record<string, string>) => true,
    submit: () => true,
  },
  setup(props, { emit }) {
    const firstInputRef = ref<InstanceType<typeof FormInput>>();
    onMounted(() => {
      firstInputRef.value?.focus();
    });
    return () =>
      h(
        "div",
        props.extraFields.map((field, index) =>
          h("div", { style: index > 0 ? { marginTop: "12px" } : undefined }, [
            h("p", { style: { marginBottom: "8px" } }, `${field.label}:`),
            h(FormInput, {
              ref: index === 0 ? firstInputRef : undefined,
              value: props.extraValues[field.key] ?? "",
              type: "text",
              placeholder: field.label,
              state: "default",
              "onUpdate:value": (value: string) => {
                emit("update:extraValues", {
                  ...props.extraValues,
                  [field.key]: value,
                });
              },
              onKeydown: (event: KeyboardEvent) => {
                if (event.key === "Enter") emit("submit");
              },
            }),
          ])
        )
      );
  },
});

// ── Composable ─────────────────────────────────────────────────────────────

export async function useMembraneSetup(
  orgId: ComputedRef<string>,
  controller: Controller,
  integrations: UseIntegrations,
  app: Ref<Integration>,
  router: Router,
  route: RouteLocationNormalizedLoaded
) {
  // Releasable controller refs acquired while the composable loads. They are
  // all collected here and released via the returned `release()`.
  const membraneDataRefs: Promise<ReleasableRef<unknown>>[] = [];

  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    // A ref may still be loading when the caller releases: release each one
    // as soon as it lands.
    for (const refPromise of membraneDataRefs) {
      void refPromise.then(releaseBestEffort);
    }
  };

  // The store's long-lived per-organization client: mutations go through it,
  // and `integrations.refresh()` below propagates them to every view.
  const membrane = computed(() => integrations.client(orgId.value));

  // Reuse the store's cached connections; a load that previously failed is
  // retried here (user-driven navigation to the settings page).
  await integrations.ensureLoaded(orgId.value, { reloadOnError: true });
  const integrationsView = integrations.forOrganization(
    computed(() => orgId.value)
  );
  const connections = computed(() =>
    integrationsView.connections.value.filter(
      (connection) => connection.integrationId === app.value.id
    )
  );

  const allWorkspaceData: WorkspaceDataItem[] = await (async () => {
    const workspaceIds = await controller.organization.workspaces.all(
      orgId.value!
    );
    const promises = workspaceIds.value.map(async (workspaceId) => {
      const membraneDataPromise = controller.integrationApp.get(workspaceId);
      membraneDataRefs.push(membraneDataPromise);
      const [workspace, membraneData] = await Promise.all([
        controller.workspace.get(workspaceId),
        membraneDataPromise,
      ]);
      const workspaceName = workspace.value?.name ?? workspaceId;
      workspace.release();
      return { workspaceId, workspaceName, membraneData };
    });
    workspaceIds.release();
    return await Promise.all(promises);
  })();

  const allDatabases = await (() => {
    const db = controller.graph.database.list();
    membraneDataRefs.push(db);
    return db;
  })();

  // ── Computed ─────────────────────────────────────────────────────────────

  const enabledDataCollections = computed(
    (): EnabledDataCollectionWithWorkspace[] =>
      allWorkspaceData.flatMap(({ workspaceId, workspaceName, membraneData }) =>
        (membraneData.value?.enabled_data_collections ?? [])
          .filter(([{ connection_id }]) =>
            connections.value.some((c) => c.id === connection_id)
          )
          .flatMap(([dataCollectionId, dataCollection]) => {
            const databaseName = match(dataCollection)
              .with({ status: "ready" }, { status: "force_refresh" }, (dc) => {
                const db = allDatabases.value?.[workspaceId]?.[dc.table];
                if (db) return db.data_name;
                window.console.warn(
                  `Database not found for table ID ${dc.table} in workspace ${workspaceId}`
                );
                return null;
              })
              .with(
                { status: "creation_pending" },
                (dc) => dc.database_display_name
              )
              .with({ status: "error" }, (dc) =>
                match(dc.last_known_state)
                  .with({ status: "ready" }, (s) => {
                    const db = allDatabases.value?.[workspaceId]?.[s.table];
                    return db?.data_name ?? null;
                  })
                  .with(
                    { status: "creation_pending" },
                    (s) => s.database_display_name
                  )
                  .exhaustive()
              )
              .exhaustive();
            if (databaseName === null) return [];
            return [
              {
                workspaceId,
                workspaceName,
                dataCollectionId,
                dataCollection,
                databaseName,
              },
            ];
          })
      )
  );

  const dataSourcesSupported = computed(() => {
    const allowed = getAllowedDataCollections(app.value.key);
    return allowed === null || allowed.length > 0;
  });

  // ── Connection management ─────────────────────────────────────────────────

  /**
   * Fetches every auth method the integration supports (OAuth, API Key, …).
   * Membrane may return more than one; the caller decides whether to let the
   * user pick (see addAccount) or just use the first (single-auth workflows).
   */
  const fetchConnectionOptions = async (): Promise<ConnectionOption[]> =>
    await membrane.value.post("/connection-options", {
      integrationId: app.value.id,
    });

  const fetchAuthOptionKey = async (): Promise<
    ConnectionOption | undefined
  > => {
    const options = await fetchConnectionOptions();
    return options[0];
  };

  /**
   * Whether a connection flow must run in the browser instead of the Membrane
   * SDK. Every interactive flow (OAuth, with or without input fields) goes
   * through {@link connectInBrowser} — the default browser on desktop, a new
   * tab on web — so the user finishes on a Routine page instead of a
   * Membrane-hosted one. Only client-credentials connections (API keys, …)
   * keep the SDK flow, which completes silently through a hidden iframe.
   */
  const useBrowserFlow = (authOptionType?: string) =>
    authOptionType !== "client-credentials";

  /**
   * Creates a new connection for this integration.
   * No name is passed — Membrane uses the authenticated account's
   * email/display name from the OAuth profile as the connection name.
   */
  const createConnection = async (
    authOptionKey?: string,
    input?: Record<string, string>,
    authOptionType?: string
  ): Promise<{ id: string } | null> => {
    const hasInput = input !== undefined && Object.keys(input).length > 0;
    const newConn = useBrowserFlow(authOptionType)
      ? await connectInBrowser({
          client: membrane.value,
          integrationId: app.value.id,
          authOptionKey,
          input,
          redirectPath: route.path,
        })
      : await membrane.value.connect({
          integrationId: app.value.id,
          allowMultipleConnections: true,
          authOptionKey,
          ...(hasInput ? { input } : {}),
        });
    await integrations.refresh(orgId.value);
    return newConn;
  };

  /**
   * Reconnects an existing connection, optionally passing freshly collected
   * input fields. Mirrors createConnection's flow selection and refresh side
   * effects.
   */
  const reconnectWithInput = async (
    connectionId: string,
    authOptionKey?: string,
    input?: Record<string, string>,
    authOptionType?: string
  ) => {
    const hasInput = input !== undefined && Object.keys(input).length > 0;
    if (useBrowserFlow(authOptionType)) {
      await connectInBrowser({
        client: membrane.value,
        integrationId: app.value.id,
        connectionId,
        authOptionKey,
        input,
        redirectPath: route.path,
      });
    } else {
      await membrane.value.connect({
        connectionId,
        authOptionKey,
        ...(hasInput ? { input } : {}),
      });
    }
    await integrations.refresh(orgId.value);
  };

  /**
   * Surfaces a failed connection attempt as a notification instead of letting
   * the rejection bubble up to the global error handler (which shows a fatal
   * error). Membrane rejects with a MembraneError when the third-party service
   * refuses the connection (e.g. "Error making request to
   * https://graph.facebook.com/v18.0: 400", Sentry FRONTEND-FFJ) or with a
   * plain `{ message }` object when the popup is blocked.
   */
  const notifyConnectError = (error: unknown) => {
    const message =
      firstStringProperty(error, ["message"]) ?? errorMessage(error);
    controller.notifications.notify(`Couldn't connect account: ${message}`);
    window.console.error("Integration connect failed", error);
  };

  /**
   * Runs the connection flow for a single chosen auth option. When the option
   * has no input fields (e.g. OAuth) it connects directly; otherwise it opens a
   * modal to collect the required fields (e.g. an API key) before connecting.
   *
   * Pass `connectionId` to reconnect an existing connection (reusing the auth
   * method it was created with) instead of creating a new one.
   */
  const connectWithOption = async (
    option: ConnectionOption | undefined,
    connectionId?: string
  ) => {
    const authOptionKey = option?.key;
    const { required: extraFields, optional: optionalFields } =
      partitionAuthInputFields(option?.inputSchema);

    const connect = (input?: Record<string, string>) =>
      connectionId
        ? reconnectWithInput(connectionId, authOptionKey, input, option?.type)
        : createConnection(authOptionKey, input, option?.type);

    /**
     * Opens the field-collection modal for a given set of fields. Extracted so
     * the same modal serves both the upfront required-field case and the
     * retry-with-optional-fields case below.
     */
    const promptForFields = (fields: AuthInputField[]) => {
      const extraValues = ref<Record<string, string>>({});
      const submit = async () => {
        try {
          await connect(extraValues.value);
        } catch (error) {
          notifyConnectError(error);
          // Keep the modal open so the user can correct the input and retry.
          return false;
        }
        if (route.query.addAccount) {
          router.replace({ query: { ...route.query, addAccount: undefined } });
        }
        close();
      };

      const { open, close } = useModal({
        component: Modal,
        attrs: {
          title: connectionId ? "Reconnect Account" : "Add Account",
          showTitleBar: true,
          showTopBar: false,
          modalButtons: [
            {
              title: connectionId ? "Reconnect" : "Connect",
              kind: "main",
              state: ref<ButtonState>("default"),
              func: submit,
            },
          ],
        },
        slots: {
          default: () =>
            h(ExtraFieldsModalBody, {
              extraFields: fields,
              extraValues: extraValues.value,
              "onUpdate:extraValues": (v: Record<string, string>) => {
                extraValues.value = v;
              },
              onSubmit: submit,
            }),
        },
      });
      open();
    };

    if (extraFields.length === 0) {
      try {
        await connect();
        return;
      } catch (error) {
        notifyConnectError(error);
        // The attempt can fail *because* an optional field was needed: Jira
        // rejects the callback with "Multiple Jira sites found: …" when the
        // account can reach more than one site, and that is the earliest
        // moment anyone can know it. Ask only now, rather than making every
        // single-site user answer upfront.
        if (optionalFields.length > 0) promptForFields(optionalFields);
        return;
      }
    }

    promptForFields(extraFields);
  };

  const addAccount = async () => {
    const options = await fetchConnectionOptions();

    // 0 or 1 auth method: behave exactly as before — connect directly (OAuth)
    // or collect the single option's input fields (API key, etc.).
    if (options.length <= 1) {
      await connectWithOption(options[0]);
      return;
    }

    // Multiple auth methods: let the user choose one first (step 1), then fall
    // through to that option's own flow (step 2).
    const { open, close } = useModal({
      component: Modal,
      attrs: {
        title: "Add Account",
        subtitle: "Choose how to connect",
        showTitleBar: true,
        showTopBar: false,
        modalButtons: [],
      },
      slots: {
        default: () =>
          h(ConnectionTypePicker, {
            options,
            onSelect: (option: ConnectionOption) => {
              close();
              void connectWithOption(option);
            },
          }),
      },
    });
    open();
  };

  /**
   * Delete a connection and all associated data sources.
   * Membrane sends a webhook to API which cleans up the database.
   */
  const deleteConnection = async (connectionId: string) => {
    await membrane.value.connection(connectionId).archive();
    await integrations.refresh(orgId.value);
  };

  const reconnectConnection = async (connectionId: string) => {
    const options = await fetchConnectionOptions();
    // Reuse the auth method the connection was originally created with, so a
    // multi-auth integration reconnects via the same type the user first chose.
    // Fall back to the first option for connections created before the auth
    // method was recorded (or single-auth integrations).
    const existing = connections.value.find((c) => c.id === connectionId);
    const option =
      options.find((o) => o.key === existing?.authOptionKey) ?? options[0];
    await connectWithOption(option, connectionId);
  };

  const updatingConnectionIds = ref(new Set<string>());
  const isUpdatingConnection = (connectionId: string) =>
    updatingConnectionIds.value.has(connectionId);
  const markUpdatingConnection = (connectionId: string) => {
    const next = new Set(updatingConnectionIds.value);
    next.add(connectionId);
    updatingConnectionIds.value = next;
  };
  const clearUpdatingConnection = (connectionId: string) => {
    const next = new Set(updatingConnectionIds.value);
    next.delete(connectionId);
    updatingConnectionIds.value = next;
  };

  /**
   * Moves this connection onto the integration's current connector version.
   * Same connection id; no reconnect. A no-op unless the connection is an
   * older version of the same connector.
   */
  const updateConnection = (connectionId: string) => {
    const existing = connections.value.find((c) => c.id === connectionId);
    if (!existing) return Promise.resolve();
    return runConnectionVersionUpdate({
      connectionId,
      isUpdating: isUpdatingConnection,
      markUpdating: markUpdatingConnection,
      clearUpdating: clearUpdatingConnection,
      update: () =>
        patchConnectionConnectorVersion({
          connectionId,
          connection: existing,
          integration: app.value,
          patch: (body) => membrane.value.connection(connectionId).patch(body),
          refresh: () => integrations.refresh(orgId.value),
        }),
      notify: (message) => {
        void controller.notifications.notify(message);
      },
      describeError: (error) =>
        firstStringProperty(error, ["message"]) ?? errorMessage(error),
    });
  };

  // ── Data source management ────────────────────────────────────────────────

  const deleteSource = async (
    workspaceId: string,
    enabledDataCollectionId: IntegrationAppEnabledDataCollectionId,
    dataCollection: IntegrationAppEnabledDataCollection
  ) => {
    // Remove any task bindings pointing at this data source's table before
    // disabling the data collection, so no stale bindings are left behind.
    const tableId = getDatabaseId(dataCollection);
    if (tableId) {
      const bindingsRef = await controller.task.binding.all();
      const bindings = bindingsRef.release() ?? {};
      const matchingIds = Object.entries(bindings)
        .filter(([, b]) => b.table_id === tableId)
        .map(([id]) => id);
      await Promise.all(
        matchingIds.map((id) => controller.task.binding.remove(id))
      );
    }
    controller.integrationApp.disableDataCollection(
      workspaceId,
      enabledDataCollectionId
    );
  };

  /**
   * Requests an immediate re-synchronization of a currently-ready data
   * collection. Re-enables it with the `force_refresh` status, carrying the
   * previous ready state so the underlying database/flow instance is
   * preserved. The API's commit inspector picks this up, schedules a refresh,
   * and resets the collection back to `ready` with a fresh `last_synced`.
   */
  const forceRefreshSource = async (
    workspaceId: string,
    enabledDataCollectionId: IntegrationAppEnabledDataCollectionId,
    dataCollection: IntegrationAppEnabledDataCollection
  ) => {
    if (dataCollection.status !== "ready") return;
    await controller.integrationApp.enableDataCollection(
      workspaceId,
      enabledDataCollectionId,
      { ...dataCollection, status: "force_refresh" }
    );
  };

  const navigateToAddDataCollection = () => {
    const appKey = app.value.key;
    if (!appKey) return;
    router.push({
      name: RouteNames.SettingsMembraneAddDataCollection,
      params: { orgId: orgId.value, appKey },
    });
  };

  const openDatabase = async (
    workspaceId: string,
    databaseId: string,
    event: Event
  ) => {
    event.stopPropagation();
    await navigateToDatabase(router, workspaceId, databaseId);
  };

  // ── Display helpers ───────────────────────────────────────────────────────

  /**
   * Generates a stable v-for key for a data collection configuration.
   * Hashes the connection_id + data_collection_key + parameters tuple.
   */
  const getSourceKey = (id: IntegrationAppEnabledDataCollectionId) => {
    const s = JSON.stringify({
      connection: id.connection_id,
      collection: id.data_collection_key,
      params: id.parameters,
    });
    let hash = 0;
    for (let i = 0; i < s.length; i++) {
      hash = (hash << 5) - hash + s.charCodeAt(i);
      hash = hash & hash;
    }
    return hash.toString();
  };

  const getAccountName = (connectionIdOrKey: string) => {
    const conn = connections.value.find(
      (c) =>
        c.id === connectionIdOrKey || c.integration?.key === connectionIdOrKey
    );
    return conn?.name || connectionIdOrKey;
  };

  const getConnectionStatus = (connection: Connection) =>
    (connection?.state ?? WorkspaceElementState.READY) ===
    WorkspaceElementState.READY;

  const getConnectionStatusText = (connection: Connection) =>
    match(connection?.state ?? WorkspaceElementState.READY)
      .with(WorkspaceElementState.READY, () => "Account is healthy")
      .otherwise(
        () =>
          "Account is unhealthy. Try to reconnect it, or delete it (but beware that it will delete all data sources for this connection) and re-create it."
      );

  const getDataCollectionStatus = (
    dc: IntegrationAppEnabledDataCollection
  ): "ready" | "pending" | "error" =>
    match(dc)
      .with({ status: "ready" }, () => "ready" as const)
      .with({ status: "creation_pending" }, () => "pending" as const)
      .with({ status: "force_refresh" }, () => "pending" as const)
      .with({ status: "error" }, () => "error" as const)
      .exhaustive();

  const getDataCollectionStatusText = (
    dc: IntegrationAppEnabledDataCollection
  ) =>
    match(dc)
      .with({ status: "ready" }, () => "Data source is synchronized")
      .with(
        { status: "creation_pending" },
        () => "Data source is being created"
      )
      .with({ status: "force_refresh" }, () => "Data source is refreshing")
      .with(
        { status: "error" },
        () => "Data source has an error, contact Routine support if it persists"
      )
      .exhaustive();

  const getDatabaseId = dataCollectionTableId;

  const getSubStatusIndicator = (
    dc: IntegrationAppEnabledDataCollection
  ): { icon: "info" | "warning"; tooltip: string } | null => {
    if (dc.status !== "ready") return null;
    const paginationStoppedHandler = (max: number) => ({
      icon: "warning" as const,
      tooltip: `This data source has reached the maximum number of objects (${max}) and additional objects will not be synchronized.`,
    });
    return match(dc.sub_status)
      .with({ "status-kind": "success" }, "success", () => null)
      .with({ "status-kind": "no_list_method" }, "no_list_method", () => ({
        icon: "info" as const,
        tooltip:
          "This data source cannot fetch external objects, but creation/update/deletion might still work.",
      }))
      .with(
        {
          "status-kind": "pagination_stopped",
          "pagination-stopped-reason-kind": "max_number_of_records",
          max_number_of_records: P.select(),
        },
        paginationStoppedHandler
      )
      .with(P.number, paginationStoppedHandler)
      .exhaustive();
  };

  /**
   * Returns a secondary detail line for a connection — the authenticated
   * account's email. Returns null when nothing useful is available or the
   * value would duplicate the connection name.
   */
  const getConnectionDetail = (connection: Connection): string | null => {
    const candidates: Array<unknown> = [
      connection.user?.fields?.["email"],
      connection.meta?.["email"],
      connection.meta?.["accountEmail"],
    ];
    for (const candidate of candidates) {
      if (
        typeof candidate === "string" &&
        candidate.length > 0 &&
        candidate !== connection.name
      ) {
        return candidate;
      }
    }
    return null;
  };

  // ── Copy helpers ──────────────────────────────────────────────────────────

  const copyIntegrationId = async () => {
    await copy(app.value.id);
    controller.notifications.notify("Integration ID copied to clipboard");
  };

  const copyConnectionId = async (connectionId: string) => {
    await copy(connectionId);
    controller.notifications.notify("Connection ID copied to clipboard");
  };

  return {
    connections,
    allWorkspaceData,
    allDatabases,
    membrane,
    enabledDataCollections,
    dataSourcesSupported,
    addAccount,
    connectWithOption,
    createConnection,
    notifyConnectError,
    fetchConnectionOptions,
    fetchAuthOptionKey,
    deleteConnection,
    reconnectConnection,
    updateConnection,
    isUpdatingConnection,
    deleteSource,
    forceRefreshSource,
    navigateToAddDataCollection,
    openDatabase,
    getSourceKey,
    getAccountName,
    getConnectionStatus,
    getConnectionStatusText,
    getDataCollectionStatus,
    getDataCollectionStatusText,
    getDatabaseId,
    getSubStatusIndicator,
    getConnectionDetail,
    copyIntegrationId,
    copyConnectionId,
    release,
  };
}

export type MembraneSetup = Awaited<ReturnType<typeof useMembraneSetup>>;
